import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Bot, Building2, Check, Copy, KeyRound, LogIn, Mail, Monitor, Moon, Palette, Pencil, Radar, Route, Send, ShieldCheck, Sun, Trash2, UserPlus, Users, Volume2, Wifi } from 'lucide-react';
import type { AiProvider, AiSettings, AutoDiscoveryRunSummary, AutoDiscoverySettings, CustomRole, DiscoveryRecent, SmtpSettings, SmtpTestResult, HostRouteSubnetApplyResult, HostRoutingSettings, Resource, SessionUser, Theme, UserGroup } from '@itr/shared';
import { accessHostFor, formatRelative, isCustomerRole } from '@itr/shared';
import { api, tokens } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme/ThemeProvider';
import {
  Badge,
  Drawer,
  EmptyState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
} from '../components/ui';
import { isWebPushSupported, subscribeWebPush, unsubscribeWebPush, webPushPermission } from '../notifications/push';
import { playAlertChime } from '../notifications/chime';
import {
  healthTransitionText,
  isAlertSpeechEnabled,
  isAlertSpeechSupported,
  setAlertSpeechEnabled,
  speakAlert,
} from '../notifications/speak';
import type { ClientRow, SiteRow } from './Clients';

type Tab = 'profile' | 'appearance' | 'alert-sounds' | 'notifications' | 'ai' | 'routing' | 'discovery' | 'users';

export function SettingsPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('profile');

  const tabs: Array<{ id: Tab; label: string; icon: typeof Bell; show: boolean }> = [
    { id: 'profile', label: 'Profile', icon: Users, show: true },
    { id: 'appearance', label: 'Appearance', icon: Palette, show: true },
    { id: 'alert-sounds', label: 'Alert sounds', icon: Volume2, show: true },
    { id: 'notifications', label: 'Notifications', icon: Bell, show: can('settings:manage') },
    { id: 'ai', label: 'AI assistance', icon: Bot, show: can('settings:manage') },
    { id: 'routing', label: 'Static routes', icon: Route, show: can('settings:manage') },
    { id: 'discovery', label: 'Discovery', icon: Radar, show: can('resource:read') },
    { id: 'users', label: 'Users & customers', icon: UserPlus, show: can('user:read') },
  ];

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Settings</h1>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 18, gap: 4, flexWrap: 'wrap' }}>
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button key={t.id} className={`btn btn-sm${tab === t.id ? ' btn-primary' : ''}`} onClick={() => setTab(t.id)}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
      </div>

      {tab === 'profile' && (
        <>
          <ProfileSettings />
          <TwoFactorSettings />
        </>
      )}
      {tab === 'appearance' && <AppearanceSettings />}
      {tab === 'alert-sounds' && <AlertSoundSettings />}
      {tab === 'notifications' && (
        <>
          <SmtpSettingsPanel />
          <NotificationSettings />
        </>
      )}
      {tab === 'ai' && <AiSettingsPanel />}
      {tab === 'routing' && <HostRoutingSettingsPanel />}
      {tab === 'discovery' && <DiscoverySettingsPanel />}
      {tab === 'users' && <UserSettings />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const { data } = useFetch(
    () => api.get<{ profile: { phone: string | null; timezone: string; notifyEmail: boolean; notifySms: boolean } }>('/api/auth/me'),
    [],
  );
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const current = { ...(data?.profile ?? {}), name: user?.name, ...form };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch('/api/auth/me', { ...form, ...(password ? { password } : {}) });
      setPassword('');
      setForm({});
      await refreshUser();
      toast('Profile updated', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={save}>
      <div className="card-header">
        <span className="card-title">Your profile</span>
      </div>
      <div className="card-body">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={String(current.name ?? '')} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </div>

        <div className="field">
          <label className="field-label">Email</label>
          <input className="input" value={user?.email ?? ''} disabled />
          <span className="field-help">Contact an administrator to change your sign-in address.</span>
        </div>

        <div className="field">
          <label className="field-label">Mobile number</label>
          <input
            className="input"
            value={String(current.phone ?? '')}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+20 100 123 4567"
          />
          <span className="field-help">Used for SMS alert pages. Include the country code.</span>
        </div>

        <div className="field">
          <label className="field-label">New password</label>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Leave blank to keep your current password"
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <div className="col" style={{ gap: 10, marginBottom: 18 }}>
          <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(current.notifyEmail)}
              onChange={(e) => setForm((f) => ({ ...f, notifyEmail: e.target.checked }))}
            />
            Email me when alerts fire
          </label>
          <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(current.notifySms)}
              onChange={(e) => setForm((f) => ({ ...f, notifySms: e.target.checked }))}
            />
            Send me SMS for critical alerts
          </label>
        </div>

        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const field = document.createElement('textarea');
    field.value = value;
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Two independent second factors an operator can turn on for their own
 * account — an authenticator app (works offline, the stronger default) and
 * an emailed code (needs no app, but only as strong as the inbox and the
 * SMTP relay behind it — see the Notifications tab). Either satisfies login;
 * both may be on at once, with the authenticator preferred when both are.
 */
function TwoFactorSettings() {
  const { data, reload } = useFetch(
    () => api.get<{ profile: { totpEnabled: boolean; emailOtpEnabled: boolean } }>('/api/auth/me'),
    [],
  );

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <ShieldCheck size={14} />
        <span className="card-title">Two-factor authentication</span>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 18 }}>
        <TotpSection enabled={data?.profile.totpEnabled ?? null} onChanged={reload} />
        <div style={{ borderTop: 'var(--itr-border-w) solid var(--itr-border)' }} />
        <EmailOtpSection enabled={data?.profile.emailOtpEnabled ?? null} onChanged={reload} />
      </div>
    </div>
  );
}

function TotpSection({ enabled, onChanged }: { enabled: boolean | null; onChanged: () => void }) {
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ secret: string; otpauthUrl: string }>('/api/auth/totp/setup');
      setSetup(res);
      setCode('');
    } catch (err) {
      toast('Could not start setup', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post<{ backupCodes: string[] }>('/api/auth/totp/enable', { code: code.trim() });
      setBackupCodes(res.backupCodes);
      setSetup(null);
      onChanged();
      toast('Authenticator app enabled', 'good');
    } catch (err) {
      toast('That code did not match', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/totp/disable', { password });
      setPassword('');
      setDisabling(false);
      onChanged();
      toast('Authenticator app turned off', 'good');
    } catch (err) {
      toast('Could not turn it off', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  if (backupCodes) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <KeyRound size={15} />
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>Save your backup codes</span>
        </div>
        <p className="subtle" style={{ fontSize: 12.5, marginBottom: 10, lineHeight: 1.55 }}>
          Each works once, in place of a code from your app, if you ever lose access to it. They are shown only this
          once — store them somewhere safe.
        </p>
        <div
          className="mono"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4px 16px',
            padding: '10px 12px',
            borderRadius: 'var(--itr-radius-sm)',
            background: 'var(--itr-surface-sunken)',
            fontSize: 13,
            marginBottom: 10,
          }}
        >
          {backupCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={async () => {
              if (await copyText(backupCodes.join('\n'))) toast('Copied', 'good');
            }}
          >
            <Copy size={12} /> Copy codes
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setBackupCodes(null)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={15} />
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>Authenticator app</span>
            {enabled !== null && <Badge intent={enabled ? 'good' : 'neutral'}>{enabled ? 'Enabled' : 'Off'}</Badge>}
          </div>
          <p className="subtle" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            Google Authenticator, 1Password, Authy or similar. Works without a network connection.
          </p>
        </div>
        {enabled && !disabling && (
          <button type="button" className="btn btn-sm" onClick={() => setDisabling(true)}>
            Turn off
          </button>
        )}
        {!enabled && !setup && (
          <button type="button" className="btn btn-sm" onClick={beginSetup} disabled={busy}>
            Set up
          </button>
        )}
      </div>

      {disabling && (
        <form className="row" style={{ gap: 8, marginTop: 10 }} onSubmit={disable}>
          <input
            className="input"
            type="password"
            placeholder="Confirm your password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ maxWidth: 240 }}
          />
          <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !password}>
            {busy ? 'Working…' : 'Confirm'}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setDisabling(false)} disabled={busy}>
            Cancel
          </button>
        </form>
      )}

      {setup && (
        <div style={{ marginTop: 12 }}>
          <p className="subtle" style={{ fontSize: 12.5, marginBottom: 8 }}>
            Scan this in your authenticator app, or enter the key manually, then confirm with the code it shows.
          </p>
          <div
            className="row"
            style={{
              gap: 8,
              padding: '9px 11px',
              borderRadius: 'var(--itr-radius-sm)',
              background: 'var(--itr-surface-sunken)',
              marginBottom: 10,
            }}
          >
            <code className="mono truncate" style={{ flex: 1, fontSize: 13, letterSpacing: '0.04em' }}>
              {setup.secret}
            </code>
            <button
              type="button"
              className="btn btn-sm btn-icon"
              onClick={async () => {
                if (await copyText(setup.secret)) toast('Copied', 'good');
              }}
              title="Copy key"
              aria-label="Copy key"
            >
              <Copy size={12} />
            </button>
          </div>
          <form className="row" style={{ gap: 8 }} onSubmit={confirm}>
            <input
              className="input"
              inputMode="numeric"
              placeholder="6-digit code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ maxWidth: 140 }}
            />
            <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !code.trim()}>
              {busy ? 'Checking…' : 'Confirm'}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setSetup(null)} disabled={busy}>
              Cancel
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function EmailOtpSection({ enabled, onChanged }: { enabled: boolean | null; onChanged: () => void }) {
  const toast = useToast();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ sentTo: string }>('/api/auth/email-otp/setup');
      setSentTo(res.sentTo);
      setCode('');
    } catch (err) {
      toast('Could not send a code', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/email-otp/enable', { code: code.trim() });
      setSentTo(null);
      onChanged();
      toast('Email codes enabled', 'good');
    } catch (err) {
      toast('That code did not match', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/email-otp/disable', { password });
      setPassword('');
      setDisabling(false);
      onChanged();
      toast('Email codes turned off', 'good');
    } catch (err) {
      toast('Could not turn it off', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={15} />
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>Email code</span>
            {enabled !== null && <Badge intent={enabled ? 'good' : 'neutral'}>{enabled ? 'Enabled' : 'Off'}</Badge>}
          </div>
          <p className="subtle" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            A 6-digit code sent to your sign-in address. No app needed, but only as reliable as the server's SMTP
            setup — see Notifications for that.
          </p>
        </div>
        {enabled && !disabling && (
          <button type="button" className="btn btn-sm" onClick={() => setDisabling(true)}>
            Turn off
          </button>
        )}
        {!enabled && !sentTo && (
          <button type="button" className="btn btn-sm" onClick={beginSetup} disabled={busy}>
            Set up
          </button>
        )}
      </div>

      {disabling && (
        <form className="row" style={{ gap: 8, marginTop: 10 }} onSubmit={disable}>
          <input
            className="input"
            type="password"
            placeholder="Confirm your password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ maxWidth: 240 }}
          />
          <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !password}>
            {busy ? 'Working…' : 'Confirm'}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setDisabling(false)} disabled={busy}>
            Cancel
          </button>
        </form>
      )}

      {sentTo && (
        <form className="row" style={{ gap: 8, marginTop: 10 }} onSubmit={confirm}>
          <span className="subtle" style={{ fontSize: 12.5 }}>
            Sent to {sentTo} —
          </span>
          <input
            className="input"
            inputMode="numeric"
            placeholder="6-digit code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ maxWidth: 140 }}
          />
          <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !code.trim()}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setSentTo(null)} disabled={busy}>
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Which sites/resources page this user with a sound on a health change —
 * independent of the admin-only alert-rule/notification-policy system.
 * "Watching a site" means every resource currently at it; the server
 * resolves that dynamically at notify time, not as a frozen snapshot here.
 */
function AlertSoundSettings() {
  const toast = useToast();
  const { data: me, reload: reloadMe } = useFetch(
    () => api.get<{ alertWatch?: { siteIds: string[]; resourceIds: string[] } }>('/api/auth/me'),
    [],
  );
  const sites = useFetch(() => api.get<SiteRow[]>('/api/sites'), []);
  const resources = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 500 }), []);

  const [siteIds, setSiteIds] = useState<Set<string>>(new Set());
  const [resourceIds, setResourceIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [pushState, setPushState] = useState(() => webPushPermission());
  const [pushBusy, setPushBusy] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(() => isAlertSpeechEnabled());

  useEffect(() => {
    if (!me?.alertWatch) return;
    setSiteIds(new Set(me.alertWatch.siteIds));
    setResourceIds(new Set(me.alertWatch.resourceIds));
  }, [me]);

  const toggleSite = (id: string) =>
    setSiteIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleResource = (id: string) =>
    setResourceIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filteredResources = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = resources.data ?? [];
    return needle ? list.filter((r) => r.name.toLowerCase().includes(needle) || (accessHostFor(r) ?? '').toLowerCase().includes(needle)) : list;
  }, [resources.data, search]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/api/me/alert-watch', { siteIds: [...siteIds], resourceIds: [...resourceIds] });
      toast('Alert sound picks saved', 'good');
    } catch (err) {
      toast('Could not save', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const result = await subscribeWebPush();
      if (result.ok) {
        setPushState('granted');
        toast('Browser notifications enabled', 'good');
      } else {
        toast('Could not enable notifications', 'bad', result.reason);
      }
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeWebPush();
      toast('Browser notifications turned off on this device', 'good');
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <Volume2 size={14} />
          <span className="card-title">This device</span>
        </div>
        <div className="card-body">
          {!isWebPushSupported() ? (
            <p className="subtle" style={{ fontSize: 13 }}>
              This browser does not support push notifications. The sound below will still play while a tab is open.
            </p>
          ) : (
            <>
              <p className="subtle" style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.55 }}>
                Enable browser notifications to hear about a watched site or resource even with the tab or browser
                closed. This is per-device — repeat it on every phone/laptop/browser you want to hear from.
              </p>
              <div className="row" style={{ gap: 8 }}>
                {pushState === 'granted' ? (
                  <button className="btn" onClick={disablePush} disabled={pushBusy}>
                    {pushBusy ? 'Working…' : 'Turn off on this device'}
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={enablePush} disabled={pushBusy || pushState === 'denied'}>
                    {pushBusy ? 'Enabling…' : pushState === 'denied' ? 'Blocked in browser settings' : 'Enable browser notifications'}
                  </button>
                )}
                <button className="btn" onClick={() => playAlertChime()}>
                  Test sound
                </button>
              </div>
              {pushState === 'denied' && (
                <p className="field-help" style={{ marginTop: 8 }}>
                  Notifications were blocked for this site — re-enable them from the browser's own site settings, then
                  reload this page.
                </p>
              )}
            </>
          )}

          {isAlertSpeechSupported() && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
              <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={speechEnabled}
                  onChange={(e) => {
                    setSpeechEnabled(e.target.checked);
                    setAlertSpeechEnabled(e.target.checked);
                  }}
                />
                Read watched alerts aloud (text-to-speech)
              </label>
              <p className="field-help" style={{ marginTop: 4 }}>
                Alongside the chime, say what changed — "Alert: firewall-01 is down" and, when it clears, "Alert
                cleared: firewall-01 is back up".
              </p>
              <button
                className="btn"
                style={{ marginTop: 8 }}
                onClick={() =>
                  speakAlert(
                    `${healthTransitionText('firewall-01', 'down', 'Acme Corp', 'HQ')} ${healthTransitionText('firewall-01', 'up', 'Acme Corp', 'HQ')}`,
                  )
                }
              >
                Test voice
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Sites</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              {siteIds.size} watched
            </span>
          </div>
          {sites.loading ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={4} cols={1} />
            </div>
          ) : (sites.data ?? []).length === 0 ? (
            <EmptyState title="No sites yet" text="Sites are set up per client." />
          ) : (
            <div className="col" style={{ gap: 4, maxHeight: 380, overflowY: 'auto', padding: 10 }}>
              {(sites.data ?? []).map((site) => (
                <label key={site.id} className="card row" style={{ padding: '9px 11px', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={siteIds.has(site.id)} onChange={() => toggleSite(site.id)} />
                  <span className="grow truncate">
                    <span style={{ fontWeight: 550, fontSize: 13 }}>{site.name}</span>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {' '}
                      · {site.resourceCount} resource{site.resourceCount === 1 ? '' : 's'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Resources</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              {resourceIds.size} watched
            </span>
          </div>
          <div style={{ padding: '10px 10px 0' }}>
            <input className="input" placeholder="Search resources…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {resources.loading ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={5} cols={1} />
            </div>
          ) : (
            <div className="col" style={{ gap: 4, maxHeight: 380, overflowY: 'auto', padding: 10 }}>
              {filteredResources.map((resource) => (
                <label
                  key={resource.id}
                  className="card row"
                  style={{ padding: '9px 11px', gap: 10, alignItems: 'center', cursor: 'pointer' }}
                >
                  <input type="checkbox" checked={resourceIds.has(resource.id)} onChange={() => toggleResource(resource.id)} />
                  <span className="grow truncate">
                    <span style={{ fontWeight: 550, fontSize: 13 }}>{resource.name}</span>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {' '}
                      · {resource.kind}
                      {accessHostFor(resource) ? ` · ${accessHostFor(resource)}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <button className="btn btn-primary" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : 'Save alert sound picks'}
      </button>
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginLeft: 8 }}
        onClick={() => void reloadMe()}
        title="Discard unsaved changes and reload"
      >
        Reset
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ */

function AppearanceSettings() {
  const { theme, themes, preference, setThemeId, setMode, setPreference, resolvedMode, reloadThemes } = useTheme();
  const { can } = useAuth();
  const toast = useToast();
  const [duplicating, setDuplicating] = useState(false);

  const palette = resolvedMode === 'dark' ? theme.dark : theme.light;

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Colour mode</span>
        </div>
        <div className="card-body">
          <div className="row" style={{ gap: 8 }}>
            {(
              [
                ['light', 'Light', Sun],
                ['dark', 'Dark', Moon],
                ['system', 'System', Monitor],
              ] as const
            ).map(([value, label, Icon]) => (
              <button
                key={value}
                className={`btn${preference.mode === value ? ' btn-primary' : ''}`}
                onClick={() => setMode(value)}
                style={{ flex: 1 }}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
          <p className="field-help" style={{ marginTop: 10 }}>
            System follows your operating system, including a scheduled switch at sunset.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Theme</span>
          <span className="grow" />
          {can('theme:manage') && (
            <button
              className="btn btn-sm"
              disabled={duplicating}
              onClick={async () => {
                setDuplicating(true);
                try {
                  const copy = await api.post<Theme>(`/api/themes/${theme.id}/duplicate`, { name: `${theme.name} copy` });
                  await reloadThemes();
                  setThemeId(copy.id);
                  toast('Theme duplicated — it is now editable', 'good');
                } finally {
                  setDuplicating(false);
                }
              }}
            >
              <Copy size={13} /> Duplicate for editing
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="grid-auto" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {themes.map((option) => {
              const optionPalette = resolvedMode === 'dark' ? option.dark : option.light;
              const active = option.id === theme.id;
              return (
                <button
                  key={option.id}
                  onClick={() => setThemeId(option.id)}
                  className="card"
                  style={{
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderColor: active ? 'var(--itr-primary)' : undefined,
                    borderWidth: active ? 2 : undefined,
                  }}
                >
                  {/* A live preview beats a name: the palette strip shows the
                      actual series colours the charts will use. */}
                  <div style={{ background: optionPalette.bg, padding: 12 }}>
                    <div
                      style={{
                        background: optionPalette.surface,
                        border: `1px solid ${optionPalette.border}`,
                        borderRadius: option.shape.radius,
                        padding: 10,
                      }}
                    >
                      <div style={{ height: 6, width: '46%', background: optionPalette.text, borderRadius: 3, opacity: 0.85 }} />
                      <div style={{ height: 5, width: '68%', background: optionPalette.textSubtle, borderRadius: 3, marginTop: 6 }} />
                      <div className="row" style={{ gap: 3, marginTop: 10 }}>
                        {optionPalette.chart.slice(0, 6).map((color, i) => (
                          <span key={i} style={{ width: 13, height: 13, borderRadius: 3, background: color }} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="row" style={{ padding: '9px 11px', borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 570 }}>{option.name}</span>
                    <span className="grow" />
                    {option.builtin && <Badge>built-in</Badge>}
                    {active && <Check size={14} style={{ color: 'var(--itr-primary)' }} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Display & accessibility</span>
        </div>
        <div className="card-body">
          <div className="field">
            <label className="field-label">Density</label>
            <div className="row" style={{ gap: 6 }}>
              {(['comfortable', 'compact'] as const).map((value) => (
                <button
                  key={value}
                  className={`btn btn-sm${(preference.density ?? theme.shape.density) === value ? ' btn-primary' : ''}`}
                  onClick={() => setPreference({ density: value })}
                  style={{ textTransform: 'capitalize' }}
                >
                  {value}
                </button>
              ))}
            </div>
            <span className="field-help">Compact fits noticeably more rows on a wall display.</span>
          </div>

          <div className="field">
            <label className="field-label">Text size — {Math.round((preference.fontScale ?? theme.typography.scale) * 100)}%</label>
            <input
              type="range"
              min={0.85}
              max={1.5}
              step={0.05}
              value={preference.fontScale ?? theme.typography.scale}
              onChange={(e) => setPreference({ fontScale: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
            <span className="field-help">Scale everything up for a screen read from across the room.</span>
          </div>

          <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={preference.highContrast ?? theme.accessibility.highContrast}
              onChange={(e) => setPreference({ highContrast: e.target.checked })}
            />
            High contrast — thicker borders and stronger focus rings
          </label>

          <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={preference.reduceMotion ?? theme.accessibility.reduceMotion}
              onChange={(e) => setPreference({ reduceMotion: e.target.checked })}
            />
            Reduce motion — disable transitions and the alert pulse
          </label>

          <div style={{ marginTop: 18 }}>
            <div className="field-label" style={{ marginBottom: 7 }}>
              Chart series colours
            </div>
            <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              {palette.chart.map((color, i) => (
                <span
                  key={i}
                  title={`Series ${i + 1} — ${color}`}
                  style={{
                    width: 34,
                    height: 26,
                    borderRadius: 'var(--itr-radius-sm)',
                    background: color,
                    border: 'var(--itr-border-w) solid var(--itr-border)',
                  }}
                />
              ))}
            </div>
            <span className="field-help">
              A fixed, colour-blind-safe order. Series take these slots in sequence and the order never rotates, so a
              filter that removes a series never repaints the ones that remain.
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function SmtpSettingsPanel() {
  const toast = useToast();
  const { user } = useAuth();
  const { data, reload } = useFetch(() => api.get<SmtpSettings>('/api/settings/smtp'), []);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState(user?.email ?? '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SmtpTestResult | null>(null);

  const current = { ...(data ?? {}), ...form } as Partial<SmtpSettings> & { password?: string; clearPassword?: boolean };
  const set = (patch: Record<string, unknown>) => {
    setTestResult(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  // Only fields the operator touched are sent, so a save never pins an .env
  // default into the database by accident.
  const payload = () => ({
    host: form.host as string | undefined,
    port: form.port === undefined || form.port === '' ? undefined : Number(form.port),
    secure: form.secure as boolean | undefined,
    username: form.username as string | undefined,
    password: current.password || undefined,
    clearPassword: current.clearPassword || undefined,
    from: form.from as string | undefined,
  });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/api/settings/smtp', payload());
      setForm({});
      await reload();
      toast('SMTP settings saved', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const out = await api.post<SmtpTestResult>('/api/settings/smtp/test', { ...payload(), to: testTo });
      setTestResult(out);
    } catch (err) {
      setTestResult({ ok: false, detail: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const dirty = Object.keys(form).length > 0;

  return (
    <form className="card" onSubmit={save} style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">Outgoing email (SMTP)</span>
      </div>
      <div className="card-body">
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.55 }}>
          The relay used for alert emails, invoices and two-factor login codes. Anything left blank falls back to the
          server's <span className="mono">SMTP_*</span> environment values.
        </p>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div className="field grow">
            <label className="field-label">Host</label>
            <input
              className="input mono"
              value={String(current.host ?? '')}
              onChange={(e) => set({ host: e.target.value })}
              placeholder="smtp.example.com"
            />
          </div>
          <div className="field" style={{ width: 110 }}>
            <label className="field-label">Port</label>
            <input
              className="input mono"
              inputMode="numeric"
              value={String(current.port ?? '')}
              onChange={(e) => set({ port: e.target.value.replace(/\D/g, '') })}
              placeholder="587"
            />
          </div>
        </div>

        <div className="field">
          <label className="field-label">Encryption</label>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className={`btn btn-sm${!current.secure ? ' btn-primary' : ''}`} onClick={() => set({ secure: false })}>
              STARTTLS / none (587, 25)
            </button>
            <button type="button" className={`btn btn-sm${current.secure ? ' btn-primary' : ''}`} onClick={() => set({ secure: true })}>
              SSL/TLS (465)
            </button>
          </div>
        </div>

        <div className="field">
          <label className="field-label">Username</label>
          <input
            className="input mono"
            autoComplete="off"
            value={String(current.username ?? '')}
            onChange={(e) => set({ username: e.target.value })}
            placeholder="Leave blank for an unauthenticated relay"
          />
        </div>

        <div className="field">
          <label className="field-label">Password</label>
          <input
            className="input mono"
            type="password"
            autoComplete="new-password"
            value={String(current.password ?? '')}
            onChange={(e) => set({ password: e.target.value, clearPassword: false })}
            placeholder={data?.hasPassword ? '•••••••••••••••• (set — leave blank to keep)' : ''}
          />
          {data?.hasPassword && !current.password && (
            <span className="field-help">
              A password is already set.{' '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ height: 'auto', padding: '0 4px', fontSize: 12.5 }}
                onClick={() => set({ clearPassword: true })}
                disabled={Boolean(current.clearPassword)}
              >
                {current.clearPassword ? 'Will be cleared on save' : 'Clear it'}
              </button>
            </span>
          )}
        </div>

        <div className="field">
          <label className="field-label">From address</label>
          <input
            className="input mono"
            value={String(current.from ?? '')}
            onChange={(e) => set({ from: e.target.value })}
            placeholder="IT Resources <noreply@example.com>"
          />
          <span className="field-help">Many providers reject mail whose From doesn't match the login account or a verified domain.</span>
        </div>

        <div className="field">
          <label className="field-label">Send a test email to</label>
          <div className="row" style={{ gap: 8 }}>
            <input className="input grow" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <button type="button" className="btn" onClick={runTest} disabled={testing || !testTo || !current.host}>
              <Mail size={14} /> {testing ? 'Testing…' : 'Send test email'}
            </button>
          </div>
          <span className="field-help">Uses the values in this form, saved or not, so you can check them before saving.</span>
          {testResult && (
            <p
              className="mono"
              style={{ fontSize: 12.5, marginTop: 8, color: testResult.ok ? 'var(--itr-success)' : 'var(--itr-critical)' }}
            >
              {testResult.ok ? '✓ ' : testResult.stage === 'connect' ? 'Could not connect / log in: ' : 'Relay refused the message: '}
              {testResult.detail}
            </p>
          )}
        </div>

        <div className="row" style={{ marginTop: 16, gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <Badge intent={data?.host ? 'good' : 'warn'}>{data?.host ? 'Configured' : 'Not configured'}</Badge>
        </div>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */

function NotificationSettings() {
  const toast = useToast();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<Array<{ channel: string; target: string; ok: boolean; error?: string }>>([]);

  const runTest = async () => {
    setTesting(true);
    setResults([]);
    try {
      const out = await api.post<typeof results>('/api/notifications/test', {
        email: email || undefined,
        phone: phone || undefined,
      });
      setResults(out);
      // Report per-channel rather than a blanket "sent": SMTP working while SMS
      // is misconfigured is the common half-broken case.
      if (out.every((r) => r.ok)) toast('All channels delivered', 'good');
      else toast('Some channels failed — see the results below', 'warn');
    } catch (err) {
      toast('Test failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Test alert delivery</span>
      </div>
      <div className="card-body">
        <p className="subtle" style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.55 }}>
          Sends through the saved SMTP settings above and the SMS credentials in the server's environment. Use this to
          confirm they work before you need them at 3am.
        </p>
        <p className="subtle" style={{ fontSize: 12.5, marginBottom: 16, lineHeight: 1.55 }}>
          This same SMTP relay is what delivers a login code to anyone who turns on email-based two-factor
          authentication, under the Profile tab — a broken relay here means a locked-out operator there, so a
          passing test email is worth keeping green.
        </p>

        <div className="field">
          <label className="field-label">Send a test email to</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className="field">
          <label className="field-label">Send a test SMS to</label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+20 100 123 4567" />
        </div>

        <button className="btn btn-primary" onClick={runTest} disabled={testing || (!email && !phone)}>
          <Send size={14} /> {testing ? 'Sending…' : 'Send test'}
        </button>

        {results.length > 0 && (
          <div className="col" style={{ gap: 7, marginTop: 18 }}>
            {results.map((result, i) => (
              <div
                key={i}
                className="row"
                style={{
                  gap: 9,
                  padding: '9px 11px',
                  borderRadius: 'var(--itr-radius-sm)',
                  background: 'var(--itr-surface-sunken)',
                  fontSize: 12.5,
                }}
              >
                <Badge intent={result.ok ? 'good' : 'bad'}>{result.channel}</Badge>
                <span className="mono grow truncate">{result.target}</span>
                {result.ok ? (
                  <Check size={14} style={{ color: 'var(--itr-success)' }} />
                ) : (
                  <span style={{ color: 'var(--itr-critical)' }}>{result.error}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AiSettingsPanel() {
  const toast = useToast();
  const { data, reload } = useFetch(() => api.get<AiSettings>('/api/ai/settings'), []);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);

  const current = { ...(data ?? {}), ...form } as Partial<AiSettings> & {
    anthropicApiKey?: string;
    clearAnthropicApiKey?: boolean;
  };
  const provider: AiProvider = (current.provider as AiProvider) ?? 'anthropic';
  const set = (patch: Record<string, unknown>) => {
    setTestResult(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/api/ai/settings', {
        provider,
        anthropicApiKey: current.anthropicApiKey || undefined,
        clearAnthropicApiKey: current.clearAnthropicApiKey || undefined,
        anthropicWorkspaceId: form.anthropicWorkspaceId,
        ollamaBaseUrl: form.ollamaBaseUrl,
        ollamaModel: form.ollamaModel,
      });
      setForm({});
      await reload();
      toast('AI settings saved', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const testOllama = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const out = await api.post<{ ok: true; models: string[] } | { ok: false; error: string }>(
        '/api/ai/settings/test-ollama',
        { baseUrl: current.ollamaBaseUrl },
      );
      setTestResult(
        out.ok
          ? { ok: true, detail: out.models.length ? `Reachable — models: ${out.models.join(', ')}` : 'Reachable — no models pulled yet.' }
          : { ok: false, detail: out.error },
      );
    } catch (err) {
      setTestResult({ ok: false, detail: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <form className="card" onSubmit={save}>
      <div className="card-header">
        <span className="card-title">AI assistance</span>
      </div>
      <div className="card-body">
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.55 }}>
          Lets the root-cause panel on alerts and tickets investigate an incident — it reads metrics, health history
          and sibling alerts, runs live ping/traceroute/port checks, and reports a finding. Choose Claude (cloud) or
          point it at a local Ollama server instead.
        </p>

        <div className="field">
          <label className="field-label">Provider</label>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className={`btn btn-sm${provider === 'anthropic' ? ' btn-primary' : ''}`}
              onClick={() => set({ provider: 'anthropic' })}
            >
              Claude (Anthropic)
            </button>
            <button
              type="button"
              className={`btn btn-sm${provider === 'ollama' ? ' btn-primary' : ''}`}
              onClick={() => set({ provider: 'ollama' })}
            >
              Ollama (local)
            </button>
          </div>
        </div>

        {provider === 'anthropic' && (
          <>
            <div className="field">
              <label className="field-label">Anthropic API key</label>
              <input
                className="input mono"
                type="password"
                autoComplete="off"
                value={String(current.anthropicApiKey ?? '')}
                onChange={(e) => set({ anthropicApiKey: e.target.value, clearAnthropicApiKey: false })}
                placeholder={data?.hasAnthropicApiKey ? '•••••••••••••••• (set — leave blank to keep)' : 'sk-ant-...'}
              />
              {data?.hasAnthropicApiKey && !current.anthropicApiKey && (
                <span className="field-help">
                  A key is already set.{' '}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ height: 'auto', padding: '0 4px', fontSize: 12.5 }}
                    onClick={() => set({ clearAnthropicApiKey: true })}
                    disabled={Boolean(current.clearAnthropicApiKey)}
                  >
                    {current.clearAnthropicApiKey ? 'Will be cleared on save' : 'Clear it'}
                  </button>
                </span>
              )}
            </div>

            <div className="field">
              <label className="field-label">Workspace ID (optional)</label>
              <input
                className="input mono"
                value={String(current.anthropicWorkspaceId ?? '')}
                onChange={(e) => set({ anthropicWorkspaceId: e.target.value })}
                placeholder="Only needed for an identity-linked key spanning multiple workspaces"
              />
            </div>
          </>
        )}

        {provider === 'ollama' && (
          <>
            <div className="field">
              <label className="field-label">Ollama server URL</label>
              <input
                className="input mono"
                value={String(current.ollamaBaseUrl ?? '')}
                onChange={(e) => set({ ollamaBaseUrl: e.target.value })}
                placeholder="http://192.168.1.50:11434"
              />
              <span className="field-help">IP or hostname of the machine running Ollama, including the port (11434 by default).</span>
            </div>

            <div className="field">
              <label className="field-label">Model</label>
              <input
                className="input mono"
                value={String(current.ollamaModel ?? '')}
                onChange={(e) => set({ ollamaModel: e.target.value })}
                placeholder="llama3.1"
              />
              <span className="field-help">Name of a model already pulled on that server. Tool calling requires a model that supports it.</span>
            </div>

            <button type="button" className="btn btn-sm" onClick={testOllama} disabled={testing || !current.ollamaBaseUrl}>
              <Wifi size={14} /> {testing ? 'Testing…' : 'Test connection'}
            </button>

            {testResult && (
              <p
                className="mono"
                style={{ fontSize: 12.5, marginTop: 10, color: testResult.ok ? 'var(--itr-success)' : 'var(--itr-critical)' }}
              >
                {testResult.detail}
              </p>
            )}
          </>
        )}

        <div className="row" style={{ marginTop: 16, gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <Badge intent={data && (provider === 'ollama' ? data.ollamaBaseUrl && data.ollamaModel : data.hasAnthropicApiKey) ? 'good' : 'warn'}>
            {data && (provider === 'ollama' ? data.ollamaBaseUrl && data.ollamaModel : data.hasAnthropicApiKey) ? 'Configured' : 'Not configured'}
          </Badge>
        </div>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */

function HostRoutingSettingsPanel() {
  const toast = useToast();
  const { data, reload } = useFetch(() => api.get<HostRoutingSettings>('/api/settings/host-routing'), []);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<HostRouteSubnetApplyResult[] | null>(null);

  const current = { ...(data ?? {}), ...form } as Partial<HostRoutingSettings> & { password?: string; clearPassword?: boolean };
  const set = (patch: Record<string, unknown>) => {
    setTestResult(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/api/settings/host-routing', {
        enabled: Boolean(current.enabled),
        host: current.host || undefined,
        port: current.port ? Number(current.port) : undefined,
        username: current.username || undefined,
        password: current.password || undefined,
        clearPassword: current.clearPassword || undefined,
        gateway: current.gateway || undefined,
        iface: current.iface || undefined,
      });
      setForm({});
      await reload();
      toast('Static route settings saved', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const out = await api.post<{ ok: true; detail: string } | { ok: false; error: string }>(
        '/api/settings/host-routing/test-connection',
        { host: current.host, port: current.port ? Number(current.port) : undefined, username: current.username, password: current.password },
      );
      setTestResult(out.ok ? { ok: true, detail: out.detail } : { ok: false, detail: out.error });
    } catch (err) {
      setTestResult({ ok: false, detail: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    if (!confirm('Add a static route for every subnet the resources live in, right now? This runs real, additive route commands over SSH.')) return;
    setSyncing(true);
    setSyncResults(null);
    try {
      const out = await api.post<{ results: HostRouteSubnetApplyResult[] }>('/api/settings/host-routing/sync-now', {});
      setSyncResults(out.results);
      const failed = out.results.filter((r) => !r.ok).length;
      toast(failed ? `Synced with ${failed} failure(s)` : `Synced ${out.results.length} subnet route(s)`, failed ? 'warn' : 'good');
      await reload();
    } catch (err) {
      toast('Sync failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <form className="card" style={{ marginBottom: 14 }} onSubmit={save}>
        <div className="card-header">
          <span className="card-title">Auto static routes</span>
        </div>
        <div className="card-body">
          <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.55 }}>
            When enabled, every private or VPN-tunnelled resource address (primary and secondary) gets an additive
            static route over SSH the moment it's added or changed — a /32 to that address via the gateway below.
            Public addresses (an ISP link, a cloud endpoint, ...) are skipped, since the gateway can't actually reach
            those. Never removes or replaces an existing route.
          </p>

          <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={Boolean(current.enabled)} onChange={(e) => set({ enabled: e.target.checked })} />
            Enable auto static routes for newly added resources
          </label>

          <div className="field">
            <label className="field-label">Host (SSH)</label>
            <input
              className="input mono"
              value={String(current.host ?? '')}
              onChange={(e) => set({ host: e.target.value })}
              placeholder="mt.voipat.com or an IP"
            />
          </div>

          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label">Port</label>
              <input
                className="input mono"
                type="number"
                value={String(current.port ?? 22)}
                onChange={(e) => set({ port: e.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 2 }}>
              <label className="field-label">Username</label>
              <input className="input mono" value={String(current.username ?? '')} onChange={(e) => set({ username: e.target.value })} />
            </div>
          </div>

          <div className="field">
            <label className="field-label">Password</label>
            <input
              className="input mono"
              type="password"
              autoComplete="off"
              value={String(current.password ?? '')}
              onChange={(e) => set({ password: e.target.value, clearPassword: false })}
              placeholder={data?.hasPassword ? '•••••••••••••••• (set — leave blank to keep)' : ''}
            />
            {data?.hasPassword && !current.password && (
              <span className="field-help">
                A password is already saved.{' '}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ height: 'auto', padding: '0 4px', fontSize: 12.5 }}
                  onClick={() => set({ clearPassword: true })}
                  disabled={Boolean(current.clearPassword)}
                >
                  {current.clearPassword ? 'Will be cleared on save' : 'Clear it'}
                </button>
              </span>
            )}
          </div>

          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label">Gateway</label>
              <input className="input mono" value={String(current.gateway ?? '19.18.17.1')} onChange={(e) => set({ gateway: e.target.value })} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label">Interface</label>
              <input className="input mono" value={String(current.iface ?? 'tap0')} onChange={(e) => set({ iface: e.target.value })} />
            </div>
          </div>

          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-sm" onClick={testConnection} disabled={testing || !current.host || !current.username}>
              <Wifi size={14} /> {testing ? 'Testing…' : 'Test connection'}
            </button>
            <Badge intent={data?.enabled && data.host && data.username ? 'good' : 'warn'}>
              {data?.enabled && data.host && data.username ? 'Configured' : 'Not configured'}
            </Badge>
          </div>

          {testResult && (
            <p className="mono" style={{ fontSize: 12.5, marginTop: 10, color: testResult.ok ? 'var(--itr-success)' : 'var(--itr-critical)' }}>
              {testResult.detail}
            </p>
          )}

          {data?.lastError && (
            <p className="field-help" style={{ marginTop: 10, color: 'var(--itr-critical)' }}>
              Last run failed: {data.lastError}
            </p>
          )}
        </div>
      </form>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Backfill existing resources</span>
        </div>
        <div className="card-body">
          <p className="subtle" style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.55 }}>
            New resources get a /32 route automatically once this is enabled. Resources that already existed before
            you turned it on won't — run this once to catch up. This routes whole subnets, one /24 per network the
            resources sit in, not one route per address: a subnet's gateway or a device nobody registered yet
            (192.168.24.1, say) is then reachable from the server too. Each subnet is then pinged from every address
            the interface holds, and the route is pinned to whichever one the far side actually answers — without
            that, a tunnel carrying two addresses sends from the one some subnets have no return path for. A subnet
            the server already has its own route for is left exactly as it is. Nothing is ever deleted, but a /24
            does outrank a broader route, so any subnet whose traffic changes direction is flagged below. What gets
            applied is also written to a file the host's boot script replays, so it survives a reboot.
          </p>
          <button className="btn" onClick={syncNow} disabled={syncing || !data?.enabled}>
            {syncing ? 'Syncing…' : 'Sync existing resources now'}
          </button>

          {syncResults && (
            <div className="col" style={{ gap: 4, marginTop: 14, maxHeight: 320, overflowY: 'auto' }}>
              {syncResults.length === 0 ? (
                <span className="subtle" style={{ fontSize: 13 }}>No resource had an address to route.</span>
              ) : (
                syncResults.map((r, i) => (
                  <div
                    key={i}
                    className="col"
                    style={{ gap: 4, padding: '7px 10px', borderRadius: 'var(--itr-radius-sm)', background: 'var(--itr-surface-sunken)', fontSize: 12.5 }}
                  >
                    <div className="row" style={{ gap: 9 }}>
                      <Badge intent={r.ok ? 'good' : 'bad'}>{r.ok ? (r.alreadyPresent ? 'already set' : 'added') : 'failed'}</Badge>
                      <span className="mono">{r.subnet}</span>
                      {r.source && <span className="mono subtle">src {r.source}</span>}
                      <span className="subtle truncate grow">
                        {r.addressCount} address{r.addressCount === 1 ? '' : 'es'}
                        {r.resourceNames.length > 0 && ` — ${r.resourceNames.join(', ')}`}
                      </span>
                    </div>
                    {r.warning && (
                      <div className="row" style={{ gap: 7, alignItems: 'flex-start' }}>
                        <Badge intent="warn">overlap</Badge>
                        <span className="subtle" style={{ fontSize: 12, lineHeight: 1.45 }}>{r.warning}</span>
                      </div>
                    )}
                    {r.unreachable && (
                      <div className="row" style={{ gap: 7, alignItems: 'flex-start' }}>
                        <Badge intent="bad">no reply</Badge>
                        <span className="subtle" style={{ fontSize: 12, lineHeight: 1.45 }}>{r.unreachable}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Automatic discovery: the sweep's switch and interval (settings:manage),
 * and what it last moved and saw (resource:read, scoped to the viewer's
 * client by the router that reported each row).
 */
function DiscoverySettingsPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const canManage = can('settings:manage');
  const settings = useFetch(() => api.get<AutoDiscoverySettings>('/api/settings/auto-discovery'), [], { enabled: canManage });
  const recent = useFetch(() => api.get<DiscoveryRecent>('/api/discovery/recent'), [], { refreshMs: 60_000 });
  const [form, setForm] = useState<Partial<AutoDiscoverySettings>>({});
  const [busy, setBusy] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const current = { ...(settings.data ?? {}), ...form } as Partial<AutoDiscoverySettings>;
  const set = (patch: Partial<AutoDiscoverySettings>) => setForm((f) => ({ ...f, ...patch }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/api/settings/auto-discovery', {
        enabled: Boolean(current.enabled),
        intervalMin: current.intervalMin ? Number(current.intervalMin) : undefined,
        updateAddresses: current.updateAddresses !== false,
      });
      setForm({});
      settings.reload();
      toast('Discovery settings saved', 'good');
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setSweeping(true);
    try {
      const out = await api.post<{ summary: AutoDiscoveryRunSummary }>('/api/settings/auto-discovery/run-now', {});
      const s = out.summary;
      toast(
        `Swept ${s.routers} router(s): ${s.devices} device(s) seen, ${s.moved} address(es) moved${s.failed ? `, ${s.failed} router(s) unreadable` : ''}`,
        s.failed ? 'warn' : 'good',
      );
      settings.reload();
      recent.reload();
    } catch (err) {
      toast('Sweep failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSweeping(false);
    }
  };

  const last = settings.data?.lastRun ?? null;
  const changes = recent.data?.changes ?? [];
  const sightings = (recent.data?.sightings ?? []).slice(0, 50);

  return (
    <>
      {canManage && (
        <form className="card" style={{ marginBottom: 14 }} onSubmit={save}>
          <div className="card-header">
            <span className="card-title">Automatic discovery</span>
          </div>
          <div className="card-body">
            <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.55 }}>
              On a timer, every enabled MikroTik's ARP table and DHCP leases are read — the same read the Discover
              card on a router runs — and each MAC's latest address is remembered. A resource whose recorded MAC turns
              up at a new address is moved to follow it, along with its <code>host</code> setting when that matched,
              but only on the word of a router at the same client, only for an online device, and never for a resource
              whose address is a hostname. To pin one resource, set <code>autoUpdateAddress</code> to <code>false</code> in
              its config.
            </p>

            <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={Boolean(current.enabled)} onChange={(e) => set({ enabled: e.target.checked })} />
              Run discovery automatically
            </label>
            <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={current.updateAddresses !== false}
                onChange={(e) => set({ updateAddresses: e.target.checked })}
              />
              Update a resource's address when its MAC moves
            </label>

            <div className="field" style={{ maxWidth: 220 }}>
              <label className="field-label">Every (minutes)</label>
              <input
                className="input mono"
                type="number"
                min={5}
                max={1440}
                value={String(current.intervalMin ?? 30)}
                onChange={(e) => set({ intervalMin: Number(e.target.value) })}
              />
            </div>

            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-sm" onClick={runNow} disabled={sweeping || settings.data?.running}>
                <Radar size={14} /> {sweeping || settings.data?.running ? 'Sweeping…' : 'Run now'}
              </button>
              <Badge intent={settings.data?.enabled ? 'good' : 'neutral'}>{settings.data?.enabled ? 'On' : 'Off'}</Badge>
              {settings.data?.lastRunAt && (
                <span className="subtle" style={{ fontSize: 12.5 }}>
                  Last sweep {formatRelative(settings.data.lastRunAt)}
                  {last ? ` — ${last.routers} router(s), ${last.devices} device(s), ${last.moved} moved${last.failed ? `, ${last.failed} unreadable` : ''}` : ''}
                </span>
              )}
            </div>
            {settings.data?.lastError && (
              <p className="subtle" style={{ fontSize: 12.5, marginTop: 8, color: 'var(--warn)' }}>{settings.data.lastError}</p>
            )}
          </div>
        </form>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Addresses moved</span>
        </div>
        <div className="card-body">
          {recent.loading && !recent.data ? (
            <SkeletonTable rows={3} cols={4} />
          ) : changes.length === 0 ? (
            <EmptyState title="Nothing has moved" text="When a known device turns up at a new address, the change is listed here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Address</th>
                    <th>Seen by</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/resources/${c.resourceId}`}>{c.resourceName}</Link>
                        {c.mac && <div className="subtle mono" style={{ fontSize: 11.5 }}>{c.mac}</div>}
                      </td>
                      <td className="mono">
                        {c.fromAddress ?? '—'} → {c.toAddress}
                      </td>
                      <td>
                        {c.viaResourceId ? <Link to={`/resources/${c.viaResourceId}`}>{c.viaResourceName}</Link> : (c.viaResourceName ?? '—')}
                        <Badge intent="neutral">{c.source === 'auto' ? 'timer' : 'scan'}</Badge>
                      </td>
                      <td title={new Date(c.ts).toLocaleString()}>{formatRelative(c.ts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Recently seen</span>
        </div>
        <div className="card-body">
          {recent.loading && !recent.data ? (
            <SkeletonTable rows={4} cols={5} />
          ) : sightings.length === 0 ? (
            <EmptyState title="No sightings yet" text="Once a sweep has run, the devices the routers listed appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>MAC</th>
                    <th>Address</th>
                    <th>Name</th>
                    <th>Seen by</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {sightings.map((d) => (
                    <tr key={d.mac}>
                      <td className="mono">{d.mac}</td>
                      <td className="mono">{d.ip}</td>
                      <td>
                        {d.resourceId ? <Link to={`/resources/${d.resourceId}`}>{d.resourceName}</Link> : (d.hostname ?? '—')}
                        {d.vendor && <div className="subtle" style={{ fontSize: 11.5 }}>{d.vendor}</div>}
                      </td>
                      <td>{d.viaResourceName ?? '—'}</td>
                      <td title={new Date(d.lastSeenAt).toLocaleString()}>
                        {formatRelative(d.lastSeenAt)}
                        {d.lastIpChangeAt && <div className="subtle" style={{ fontSize: 11.5 }}>address changed {formatRelative(d.lastIpChangeAt)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function UserSettings() {
  const { can, user: me } = useAuth();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SessionUser | null>(null);

  const users = useFetch(() => api.get<SessionUser[]>('/api/users'), []);
  const customers = useFetch(() => api.get<ClientRow[]>('/api/customers'), []);
  const groups = useFetch(() => api.get<UserGroup[]>('/api/access/groups'), []);
  const roles = useFetch(() => api.get<CustomRole[]>('/api/access/roles'), []);
  // Custom roles (built via Access control → Roles) are a real, assignable
  // primary role — `users.role` is resolved by name, not restricted to the
  // built-in literal set — but the Add/Edit forms only ever offered the
  // built-ins. Anything not in that fixed set shows up here too.
  const customRoles = (roles.data ?? []).filter((r) => !r.builtin);

  const canManage = can('user:manage');
  // A user cannot remove their own account from this list (matches the
  // per-row guard below) — excluding it from the selectable set means
  // "select all" can never silently include it either.
  const selectableIds = (users.data ?? []).filter((u) => u.id !== me?.id).map((u) => u.id);
  const selection = useSelection(selectableIds);

  const bulkRemoveUsers = async () => {
    if (!confirm(`Remove ${selection.count} user(s)?`)) return;
    const result = await runBulk(selection.selectedIds, (id) => api.delete(`/api/users/${id}`));
    const { message, intent } = bulkResultMessage('Removed', result);
    toast(message, intent);
    selection.clear();
    users.reload();
  };

  return (
    <>
      {canManage && selectableIds.length > 0 && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm btn-danger" onClick={bulkRemoveUsers}>
            <Trash2 size={12} /> Remove
          </button>
        </SelectionBar>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Users</span>
          <span className="grow" />
          {can('user:manage') && (
            <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>
              <UserPlus size={13} /> Add user
            </button>
          )}
        </div>
        {users.loading ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={4} cols={3} />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canManage && selectableIds.length > 0 && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th>Name</th>
                  <th>Email</th>
                  <th style={{ width: 110 }}>Role</th>
                  <th style={{ width: 76 }} />
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((user) => (
                  <tr key={user.id}>
                    {canManage && selectableIds.length > 0 && (
                      <td>{user.id !== me?.id && <RowCheckbox selection={selection} id={user.id} />}</td>
                    )}
                    <td style={{ fontWeight: 550 }}>{user.name}</td>
                    <td className="subtle">{user.email}</td>
                    <td>
                      <Badge intent={user.role === 'owner' || user.role === 'admin' ? 'info' : 'neutral'}>{user.role}</Badge>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
                        {canManage && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditingUser(user)} aria-label="Edit user" title="Edit">
                            <Pencil size={13} />
                          </button>
                        )}
                        {can('user:manage') && (me?.role === 'owner' || me?.role === 'admin') && !me.impersonatedBy && user.id !== me.id && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={async () => {
                              try {
                                const res = await api.post<{ accessToken: string }>(`/api/users/${user.id}/connect-as`);
                                tokens.connectAs(res.accessToken);
                                window.location.assign('/');
                              } catch (err) {
                                toast(err instanceof Error ? err.message : String(err), 'bad');
                              }
                            }}
                            aria-label={`Connect as ${user.name}`}
                            title={`Connect as ${user.name} — see and do what this role can, for up to two hours, logged under your name`}
                          >
                            <LogIn size={13} />
                          </button>
                        )}
                        {can('user:manage') && user.id !== me?.id && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={async () => {
                              if (!confirm(`Remove ${user.name}?`)) return;
                              await api.delete(`/api/users/${user.id}`);
                              users.reload();
                              toast('User removed', 'good');
                            }}
                            aria-label="Remove user"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Customers</span>
          <span className="grow" />
          <Link className="btn btn-sm" to="/clients">
            <Building2 size={13} /> Manage clients &amp; sites
          </Link>
        </div>
        {(customers.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Building2 size={20} />}
            title="No customers"
            text="Customers scope resources, tickets and billing to a tenant."
            action={
              <Link className="btn btn-primary" to="/clients">
                <Building2 size={15} /> Add a client
              </Link>
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Contact</th>
                  <th style={{ width: 80 }}>Sites</th>
                  <th style={{ width: 100 }}>Resources</th>
                  <th style={{ width: 90 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(customers.data ?? []).map((customer) => (
                  <tr key={customer.id}>
                    <td style={{ fontWeight: 550 }}>
                      <Link to={`/clients/${customer.id}`}>{customer.name}</Link>
                    </td>
                    <td className="mono subtle">{customer.code}</td>
                    <td className="subtle truncate" style={{ maxWidth: 220, fontSize: 12.5 }}>
                      {customer.contactEmail ?? customer.contactPhone ?? '—'}
                    </td>
                    <td className="subtle">{customer.siteCount || '—'}</td>
                    <td className="subtle">{customer.resourceCount || '—'}</td>
                    <td>
                      <Badge intent={customer.active ? 'good' : 'neutral'}>{customer.active ? 'active' : 'inactive'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title="Add a user">
        <AddUserForm
          customers={customers.data ?? []}
          customRoles={customRoles}
          onSaved={() => {
            setAddOpen(false);
            users.reload();
            toast('User created', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editingUser)} onClose={() => setEditingUser(null)} title={editingUser ? `Edit — ${editingUser.name}` : ''}>
        {editingUser && (
          <EditUserForm
            user={editingUser}
            customers={customers.data ?? []}
            groups={groups.data ?? []}
            customRoles={customRoles}
            onSaved={() => {
              setEditingUser(null);
              users.reload();
              groups.reload();
              toast('User updated', 'good');
            }}
          />
        )}
      </Drawer>
    </>
  );
}

function AddUserForm({
  customers,
  customRoles,
  onSaved,
}: {
  customers: Array<{ id: string; name: string }>;
  customRoles: CustomRole[];
  onSaved: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'engineer', customerId: '', phone: '' });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/users', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        customerId: isCustomerRole(form.role) ? form.customerId : null,
        phone: form.phone || null,
        notifySms: Boolean(form.phone),
      });
      onSaved();
    } catch (err) {
      toast('Could not create the user', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} />
      </div>
      <div className="field">
        <label className="field-label">Email</label>
        <input className="input" type="email" required value={form.email} onChange={set('email')} />
      </div>
      <div className="field">
        <label className="field-label">Temporary password</label>
        <input className="input" type="password" required minLength={8} value={form.password} onChange={set('password')} autoComplete="new-password" />
      </div>
      <div className="field">
        <label className="field-label">Role</label>
        <select className="select" value={form.role} onChange={set('role')}>
          <option value="admin">Admin — full access</option>
          <option value="engineer">Engineer — monitor, fix, reveal credentials</option>
          <option value="technician">Technician — works assigned tickets; server logs in, never sees passwords</option>
          <option value="billing">Billing — accounts and invoices</option>
          <option value="viewer">Viewer — read only, cannot reveal secrets</option>
          <option value="customer">Client — Viewer, sees only their own tenant</option>
          <option value="customer_admin">Client — Admin, full access within their own tenant</option>
          {customRoles.length > 0 && (
            <optgroup label="Custom roles">
              {customRoles.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      {isCustomerRole(form.role) && (
        <div className="field">
          <label className="field-label">Customer</label>
          <select className="select" required value={form.customerId} onChange={set('customerId')}>
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="field-help">Every query this user makes is locked to this customer, in the data layer.</span>
        </div>
      )}
      {!isCustomerRole(form.role) && customRoles.some((r) => r.name === form.role) && (
        <p className="field-help" style={{ marginTop: -6, marginBottom: 14 }}>
          Custom roles are treated as staff accounts, not client accounts — even one named after a client, this role
          is <strong>not</strong> locked to any one tenant. Only the built-in "Client — Viewer/Admin" roles are.
        </p>
      )}
      <div className="field">
        <label className="field-label">Mobile (for SMS pages)</label>
        <input className="input" value={form.phone} onChange={set('phone')} placeholder="+20 100 123 4567" />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Creating…' : 'Create user'}
      </button>
    </form>
  );
}

function EditUserForm({
  user,
  customers,
  groups,
  customRoles,
  onSaved,
}: {
  user: SessionUser;
  customers: Array<{ id: string; name: string }>;
  groups: UserGroup[];
  customRoles: CustomRole[];
  onSaved: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<{ name: string; email: string; password: string; role: string; customerId: string; phone: string }>({
    name: user.name,
    email: user.email,
    password: '',
    role: user.role,
    customerId: user.customerId ?? '',
    phone: '',
  });
  // The groups this user currently belongs to, so saving only touches groups
  // whose membership actually changed rather than rewriting every group.
  const [groupIds, setGroupIds] = useState<Set<string>>(new Set(groups.filter((g) => g.memberIds.includes(user.id)).map((g) => g.id)));
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleGroup = (id: string) => {
    setGroupIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/api/users/${user.id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        customerId: isCustomerRole(form.role) ? form.customerId : null,
        ...(form.password ? { password: form.password } : {}),
      });

      // Group membership lives on the group, not the user — only the groups
      // whose checked state actually changed need a write.
      const originalIds = new Set(groups.filter((g) => g.memberIds.includes(user.id)).map((g) => g.id));
      const changed = groups.filter((g) => groupIds.has(g.id) !== originalIds.has(g.id));
      await Promise.all(
        changed.map((g) => {
          const memberIds = groupIds.has(g.id) ? [...g.memberIds, user.id] : g.memberIds.filter((id) => id !== user.id);
          return api.put(`/api/access/groups/${g.id}`, {
            name: g.name,
            description: g.description,
            customerId: g.customerId,
            roleIds: g.roleIds,
            memberIds,
          });
        }),
      );

      onSaved();
    } catch (err) {
      toast('Could not update the user', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} />
      </div>
      <div className="field">
        <label className="field-label">Email</label>
        <input className="input" type="email" required value={form.email} onChange={set('email')} />
      </div>
      <div className="field">
        <label className="field-label">New password</label>
        <input className="input" type="password" minLength={8} value={form.password} onChange={set('password')} autoComplete="new-password" placeholder="Leave blank to keep the current password" />
      </div>
      <div className="field">
        <label className="field-label">Role</label>
        <select className="select" value={form.role} onChange={set('role')}>
          <option value="owner">Owner — full access, including instance ownership</option>
          <option value="admin">Admin — full access</option>
          <option value="engineer">Engineer — monitor, fix, reveal credentials</option>
          <option value="technician">Technician — works assigned tickets; server logs in, never sees passwords</option>
          <option value="billing">Billing — accounts and invoices</option>
          <option value="viewer">Viewer — read only, cannot reveal secrets</option>
          <option value="customer">Client — Viewer, sees only their own tenant</option>
          <option value="customer_admin">Client — Admin, full access within their own tenant</option>
          {customRoles.length > 0 && (
            <optgroup label="Custom roles">
              {customRoles.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      {isCustomerRole(form.role) && (
        <div className="field">
          <label className="field-label">Customer</label>
          <select className="select" required value={form.customerId} onChange={set('customerId')}>
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="field-help">Every query this user makes is locked to this customer, in the data layer.</span>
        </div>
      )}
      {!isCustomerRole(form.role) && customRoles.some((r) => r.name === form.role) && (
        <p className="field-help" style={{ marginTop: -6, marginBottom: 14 }}>
          Custom roles are treated as staff accounts, not client accounts — even one named after a client, this role
          is <strong>not</strong> locked to any one tenant. Only the built-in "Client — Viewer/Admin" roles are.
        </p>
      )}

      {groups.length > 0 && (
        <>
          <div className="field-label" style={{ marginBottom: 7 }}>
            Groups — {groupIds.size} selected
          </div>
          <div className="col" style={{ gap: 4, marginBottom: 18, maxHeight: 220, overflowY: 'auto' }}>
            {groups.map((group) => (
              <label
                key={group.id}
                className="row"
                style={{ gap: 9, padding: '7px 9px', borderRadius: 'var(--itr-radius-sm)', background: 'var(--itr-surface-sunken)', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={groupIds.has(group.id)} onChange={() => toggleGroup(group.id)} />
                <span style={{ fontSize: 13 }}>{group.name}</span>
                <span className="grow" />
                <span className="subtle" style={{ fontSize: 11.5 }}>
                  {group.roleIds.length} role{group.roleIds.length === 1 ? '' : 's'}
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}
